import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import { Loader, Button, Pill, Card, Modal, Input, Select, Textarea } from "@/components/ui/Primitives";
import { MapPin, BedDouble, Bath, Calendar, ArrowRight, Home, Building2, Play, ChevronLeft, ChevronRight, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EMAIL = "contact@centralrentals.ca";

export default function PublicProperty() {
  const { propertyId } = useParams();
  const [search] = useSearchParams();
  const ref = search.get("ref");
  const [data, setData] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  // Stash referral code so application/signup flows can pick it up.
  useEffect(() => {
    if (ref) {
      try { localStorage.setItem("crc_ref", ref); } catch {}
    }
  }, [ref]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/public/property/${propertyId}`);
        if (!active) return;
        setData(data);
        // try to load self-tour slots
        try {
          const sl = await api.get(`/tour-slots?property_id=${propertyId}`);
          if (active) setSlots(Array.isArray(sl.data) ? sl.data : (sl.data?.slots || []));
        } catch {}
      } catch {
        if (active) setData(null);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [propertyId]);

  const images = useMemo(() => {
    if (!data) return [];
    const property = data.property || data;
    const raw = property.images || property.gallery || property.photos || [];
    const list = Array.isArray(raw) ? raw.map((i) => typeof i === "string" ? i : (i.url || i.image_url)).filter(Boolean) : [];
    if (list.length === 0 && property.image_url) list.push(property.image_url);
    return list;
  }, [data]);

  const tours = (data?.tours || []).filter((t) => t.video_url);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader/></div>;
  if (!data) return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader light/>
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Building2 className="w-16 h-16 mx-auto text-slate-300"/>
        <h1 className="font-display text-3xl font-bold mt-6 text-slate-900">Property not found</h1>
        <p className="text-slate-600 mt-2">This listing may have been removed.</p>
        <Link to="/"><Button className="mt-6">Back home</Button></Link>
      </div>
    </div>
  );

  const property = data.property || data;
  const units = data.units || [];
  const hero = images[galleryIdx] || property.image_url;

  return (
    <div className="bg-white min-h-screen">
      <PublicHeader light/>

      {/* Hero gallery */}
      <div className="relative h-[50vh] min-h-[400px] bg-slate-200 overflow-hidden group">
        {hero ? (
          <img src={hero} alt={property.name} className="w-full h-full object-cover transition-opacity duration-300"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><Building2 className="w-24 h-24"/></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"/>

        {images.length > 1 && (
          <>
            <button data-testid="gallery-prev" onClick={() => setGalleryIdx((i) => (i - 1 + images.length) % images.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 border border-white/30 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft className="w-5 h-5"/>
            </button>
            <button data-testid="gallery-next" onClick={() => setGalleryIdx((i) => (i + 1) % images.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 border border-white/30 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight className="w-5 h-5"/>
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {images.map((_, i) => (
                <button key={i} onClick={() => setGalleryIdx(i)} className={`w-2 h-2 rounded-full transition-all ${galleryIdx === i ? "bg-white w-6" : "bg-white/50 hover:bg-white/80"}`}/>
              ))}
            </div>
          </>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-7xl mx-auto text-white">
            <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter">{property.name}</h1>
            <div className="mt-3 flex items-center gap-2 text-white/80 text-lg">
              <MapPin className="w-5 h-5"/> {property.address}
            </div>
          </div>
        </div>

        {tours.length > 0 && (
          <button onClick={() => { setActiveVideo(tours[0]); setTourOpen(true); }} data-testid="virtual-tour-button"
            className="absolute top-6 right-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/15 hover:bg-white text-white hover:text-slate-900 border border-white/30 backdrop-blur font-semibold text-sm transition-all">
            <Play className="w-4 h-4 fill-current"/> Virtual tour
          </button>
        )}
      </div>

      {/* Thumbnail rail */}
      {images.length > 1 && (
        <div className="border-b border-slate-100 bg-slate-50/60">
          <div className="max-w-7xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto">
            {images.map((src, i) => (
              <button key={i} data-testid={`thumb-${i}`} onClick={() => setGalleryIdx(i)}
                className={`flex-none w-24 h-16 rounded-md overflow-hidden border-2 transition-all ${galleryIdx === i ? "border-green-700" : "border-transparent hover:border-slate-300"}`}>
                <img src={src} alt={`Property photo ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Available units */}
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Available units</h2>
            <p className="text-slate-500 text-sm">{units.length} unit{units.length === 1 ? "" : "s"} at this property</p>
          </div>

          {units.length === 0 ? (
            <Card className="p-10 text-center">
              <Home className="w-12 h-12 mx-auto text-slate-300 mb-3"/>
              <h3 className="font-display text-lg font-semibold">No public units listed</h3>
              <p className="text-sm text-slate-500 mt-1">Contact the property manager for availability.</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {units.map((u) => (
                <Card key={u.unit_id} className="p-6 card-lift" data-testid={`public-unit-${u.unit_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display text-xl font-bold text-slate-900">{u.label}</div>
                      <div className="mt-3 flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1"><BedDouble className="w-4 h-4"/> {u.bedrooms} BR</span>
                        <span className="flex items-center gap-1"><Bath className="w-4 h-4"/> {u.bathrooms} BA</span>
                      </div>
                    </div>
                    <Pill tone={u.tenant_id ? "slate" : "green"}>{u.tenant_id ? "Occupied" : "Available"}</Pill>
                  </div>
                  <div className="mt-5 pt-5 border-t border-slate-100 flex items-baseline justify-between">
                    <div>
                      <div className="font-display text-2xl font-bold text-slate-950">{formatCents(u.rent_amount, u.currency?.toUpperCase() || "CAD")}</div>
                      <div className="text-xs text-slate-500">per month</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Virtual tours block */}
          {tours.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">Virtual tours</h2>
              <p className="text-slate-500 text-sm mb-4">Walk through the property without leaving home.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {tours.slice(0, 4).map((t) => (
                  <button key={t.tour_id} onClick={() => { setActiveVideo(t); setTourOpen(true); }} data-testid={`tour-thumb-${t.tour_id}`}
                    className="text-left rounded-xl overflow-hidden border border-slate-200 bg-white card-lift">
                    <div className="aspect-video bg-slate-950 relative flex items-center justify-center text-white">
                      <Sparkles className="absolute top-3 left-3 w-4 h-4 text-lime-300"/>
                      <Play className="w-10 h-10 fill-current"/>
                    </div>
                    <div className="p-3">
                      <div className="font-semibold text-slate-900 truncate">{t.title || "Virtual tour"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Tap to play</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
          <Card className="p-6">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-green-800">Interested?</div>
            <h3 className="font-display text-2xl font-bold mt-2 text-slate-900">Apply in minutes</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">Submit a no-obligation application. We'll review your profile and get back to you fast.</p>
            {ref && (
              <div className="mt-4 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-center gap-2" data-testid="referral-banner">
                <span className="font-bold">Referred by</span>
                <span className="font-mono font-bold">{ref}</span>
                <span className="text-emerald-700">— bonus credit applied at signup</span>
              </div>
            )}
            <Link to={`/apply/${propertyId}${ref ? `?ref=${ref}` : ""}`}>
              <Button className="w-full mt-5" data-testid="public-apply-button">
                Apply now <ArrowRight className="w-4 h-4"/>
              </Button>
            </Link>
            <Button variant="secondary" className="w-full mt-2" onClick={() => setBookOpen(true)} data-testid="public-book-tour">
              <Calendar className="w-4 h-4"/> Book a self-guided tour
            </Button>
            <div className="mt-5 pt-5 border-t border-slate-100 space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-green-700"/> Review in 2–3 business days</div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-green-700"/> No application fee</div>
            </div>
          </Card>

          <Card className="p-5 bg-slate-50">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">Talk to the team</div>
            <a href={`mailto:${EMAIL}`} className="mt-3 flex items-center gap-2.5 text-slate-900 font-semibold hover:text-green-800" data-testid="public-email">
              <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center"><Mail className="w-4 h-4 text-green-800"/></div>
              <span className="truncate">{EMAIL}</span>
            </a>
          </Card>
        </aside>
      </div>

      {/* Video tour modal */}
      <Modal open={tourOpen} onClose={() => setTourOpen(false)} title={activeVideo?.title || "Virtual tour"} testid="virtual-tour-modal">
        {activeVideo && (
          <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
            {/youtube|youtu\.be/.test(activeVideo.video_url) ? (
              <iframe
                src={`https://www.youtube.com/embed/${extractYouTubeId(activeVideo.video_url)}?autoplay=1`}
                title={activeVideo.title}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
              />
            ) : /vimeo/.test(activeVideo.video_url) ? (
              <iframe
                src={`https://player.vimeo.com/video/${activeVideo.video_url.split("/").pop()}`}
                title={activeVideo.title}
                className="w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            ) : (
              <video src={activeVideo.video_url} controls autoPlay className="w-full h-full"/>
            )}
          </div>
        )}
      </Modal>

      <SelfTourBooking open={bookOpen} onClose={() => setBookOpen(false)} propertyId={propertyId} slots={slots}/>
    </div>
  );
}

function SelfTourBooking({ open, onClose, propertyId, slots }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", scheduled_at: "", slot_id: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/self-tour/book", {
        property_id: propertyId,
        full_name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        scheduled_at: form.slot_id ? null : (form.scheduled_at || null),
        slot_id: form.slot_id || null,
        notes: form.notes || null,
      });
      setDone(true);
      toast.success("Tour booked — confirmation sent to your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book tour");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => { onClose(); setTimeout(() => setDone(false), 200); }} title="Book a self-guided tour" testid="self-tour-modal">
      {done ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 mx-auto rounded-full bg-green-100 text-green-700 flex items-center justify-center"><Calendar className="w-6 h-6"/></div>
          <div className="mt-4 font-display text-xl font-bold text-slate-900">You're booked!</div>
          <p className="text-sm text-slate-500 mt-2">Check your email for the entry code and directions.</p>
          <Button className="mt-5" onClick={onClose}>Close</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Input label="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="tour-book-name"/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testid="tour-book-email"/>
            <Input label="Phone" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} testid="tour-book-phone"/>
          </div>
          {slots.length > 0 ? (
            <Select label="Pick a slot" value={form.slot_id} onChange={(e) => setForm({ ...form, slot_id: e.target.value })} testid="tour-book-slot">
              <option value="">Any time</option>
              {slots.map((s) => <option key={s.slot_id} value={s.slot_id}>{formatDate(s.start_at)} · {new Date(s.start_at).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}</option>)}
            </Select>
          ) : (
            <Input label="Preferred date & time" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} testid="tour-book-datetime"/>
          )}
          <Textarea label="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything we should know?"/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="tour-book-submit">{busy ? "Booking…" : "Book my tour"}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function extractYouTubeId(url) {
  const re = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/;
  const m = url.match(re);
  return m ? m[1] : "";
}
